import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, Download } from "lucide-react";

const POLL_MS = 3000;

export const RunAnalysisPanel = ({ companyId, clientName, companyName, crsScore, rmReportGeneratedAt, rmReportGeneratedBy, onDone }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const poll = async (jobId) => {
    try {
      const { data } = await api.get(`/ai/analyze/status/${jobId}`);
      setJob(data);
      if (data.status === "done") {
        toast.success(`Report generated for ${companyName || clientName} — score ${data.result?.readiness_score ?? data.readiness_score ?? "—"}/100`);
        onDone?.(data);
        return;
      }
      if (data.status === "failed") { toast.error(data.error || "Analysis failed"); return; }
      timer.current = setTimeout(() => poll(jobId), POLL_MS);
    } catch {
      timer.current = setTimeout(() => poll(jobId), POLL_MS);
    }
  };

  const start = async () => {
    const msg = `Run the Capital Readiness analysis and generate the report for ${companyName || clientName}?\n\n` +
      "This unlocks the report without payment, scores the business from its profile and documents, emails the client, and moves them to pipeline stage 3.";
    if (!window.confirm(msg)) return;
    setStarting(true);
    try {
      const { data } = await api.post(`/rm/clients/${companyId}/analyze`);
      setJob({ job_id: data.job_id, status: data.status });
      timer.current = setTimeout(() => poll(data.job_id), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the analysis");
    } finally { setStarting(false); }
  };

  const [downloading, setDownloading] = useState(false);
  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/rm/clients/${companyId}/report.pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      a.download = `${(companyName || clientName || "client").replace(/\s+/g, "-").toLowerCase()}-capital-readiness-report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Report PDF downloaded");
    } catch (e) {
      let msg = "Couldn't download the report";
      try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* ignore */ }
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const running = job && (job.status === "queued" || job.status === "running");
  const failed = job?.status === "failed";
  const done = job?.status === "done";
  const score = done ? (job.result?.readiness_score ?? job.readiness_score) : crsScore;
  const hasReport = !!(rmReportGeneratedAt || crsScore || done);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`rm-analysis-panel-${companyId}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-extrabold text-slate-900">Capital Readiness Analysis</p>
          <p className="mt-0.5 text-[11px] text-slate-500">
            Runs the full CRS analysis from the client&apos;s quiz answers, business profile and documents and generates their
            report — no Paystack checkout. The client is emailed from capital@clinq.africa.
          </p>
          {(rmReportGeneratedAt || done) && (
            <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700" data-testid={`rm-analysis-generated-${companyId}`}>
              <CheckCircle2 size={11} /> Report generated by {done ? "you" : (rmReportGeneratedBy || "RM")} · {(done ? new Date().toISOString() : rmReportGeneratedAt).slice(0, 10)}
              {score ? ` · CRS ${score}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasReport && (
            <button type="button" onClick={downloadReport} disabled={downloading || running}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#0A8B85] px-3.5 py-2 text-xs font-bold text-[#0A8B85] hover:bg-[#0A8B85]/10 disabled:opacity-50"
              data-testid={`rm-download-report-${companyId}`}>
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {downloading ? "Preparing PDF…" : "Download report PDF"}
            </button>
          )}
          <button type="button" onClick={start} disabled={starting || running}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#004B62] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#00303F] disabled:opacity-50"
            data-testid={`rm-run-analysis-${companyId}`}>
            {(starting || running) ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {running ? "Analysing…" : (hasReport ? "Re-run Capital Readiness Analysis" : "Run Capital Readiness Analysis")}
          </button>
        </div>
      </div>
      {running && (
        <p className="mt-2 text-[11px] text-slate-500" data-testid={`rm-analysis-status-${companyId}`}>
          Reading documents and scoring the six pillars — this usually takes 30–90 seconds. You can keep working; we&apos;ll update here.
        </p>
      )}
      {failed && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-[11px] text-red-700" data-testid={`rm-analysis-error-${companyId}`}>
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {job.error || "Analysis failed. Please try again."}
        </p>
      )}
    </div>
  );
};
